/**
 * Layout Engine service to get/update layout configuration
 */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { ILayoutConfigParams } from '../interfaces/layout-config-params.interface';
import { take, tap } from 'rxjs/operators';
import { ConfigurationStoreHandler } from '../../../app/configuration/services/configuration-store-handler.service';
import { get } from 'lodash';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import {NavigationConfigService} from "@ispm/navigation/services/navigationConfig.service";
import { ActivatedRoute, Router } from '@angular/router';

@Injectable()
export class LayoutEngineService {
  static CONFIG_EXPIRY_DURATION = 30000;
  private cached: Map<string, string> = new Map();
  constructor(private _ispmHttpClient: IspmHttpClient,
              private _modalLoader: ModalLoaderNgjsService,
              private _configurationStoreHandler: ConfigurationStoreHandler,
              private _formHandler: FormHandlerService,
              private _navigationConfigService: NavigationConfigService,
              private router: Router,
              private activatedRoute: ActivatedRoute) { }

  /**
   * Get layout engine configuration by cancer type and organization
   * @returns {ConnectableObservable<any>}
   */
  getLayoutConfig(params: ILayoutConfigParams): Observable<{ }> {
    params.application = this._navigationConfigService.getCurrentApplicationId();
    const paramsKey = JSON.stringify(params);
    const configId = this.cached.get(paramsKey);
    if (configId) {
      let resourceType = get(params, 'resourceType', 'Layout');
      return this._configurationStoreHandler.getConfigById(resourceType, configId).take(1).map(layoutResource => layoutResource.config);
    } else {
      const req = this._ispmHttpClient.get('layout/config', params).pipe(tap((config) => {
        this.preFetchViewForms(config['resource']);
        this._configurationStoreHandler.setConfiguration(config['resource']);
        this.cached.set(paramsKey, config['resource'].id);
      })).map(res => {
        if (res && res['resource']) {
          return res['resource'].config;
        } else {
          this.showErrorMessagePopup('Configuration Not Defined',
            'There are no default layout configurations defined for your organization. Please ask your administrator to add a default configuration or cancer type specific configuration.',
            'ALERT',
            'Pathologist');
        }
      }).publish();
      req.connect();
       return req;
    }
  }

  public showErrorMessagePopup(title, message, modalType, nextState, queryParams?:any) {
    this._modalLoader.popupModal('confirm',
      ConfirmModalLoaderPayload.builder().
      setTitle(title).
      setContent(message)
        .setApproveButtonText('OK'),
      modalType ? modalType : 'ALERT'
    ).subscribe(() => {
      let state = nextState ? nextState : 'Pathologist';
      this.router.navigate([state], {relativeTo: this.activatedRoute, queryParams: queryParams});
    });
  }

  public preFetchViewForms(layoutConfiguration) {
    const viewFormIds = get(layoutConfiguration, 'preFetchForms', []);
    const preFetchedForms = get(layoutConfiguration, 'preFetchedForms', []);
    if (preFetchedForms.length > 0 ) {
      this._formHandler.addFormstoRequestSet(preFetchedForms);
    }
    else if (viewFormIds.length > 0 ) {
      viewFormIds.map((formId) => {
        this._formHandler.getForm(formId);
      });
    }
  }
}
